// src/pages/ClinicalDataCollector/components/output/HeartFailureSection.jsx

import React, { useMemo } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import SpaIcon from '@mui/icons-material/Spa';
import { useSelector } from 'react-redux';

// 🔹 Data simulada
import heartFailureSectionData from '../data/heartFailureSectionData.json';

const HeartFailureSection = () => {
  const { PatientSelect } = useSelector(
    (state) => state.patientMapSelect || {}
  );

  const patients = heartFailureSectionData?.patients || [];

  const selectedPatient = useMemo(() => {
    if (!patients.length) return null;

    if (PatientSelect?.memberId) {
      const base =
        (PatientSelect.memberId.length || 1) +
        (PatientSelect.age || 0) * 23;
      const idx = Math.abs(base) % patients.length;
      return patients[idx];
    }

    const randomIdx = Math.floor(Math.random() * patients.length);
    return patients[randomIdx];
  }, [PatientSelect, patients]);

  if (!selectedPatient) return null;

  const {
    name,
    profile,
    code,
    description,
    nyhaClass,
    labs,
    immunizations,
    treatment,
    lifestyle
  } = selectedPatient;

  return (
    <Card elevation={3} sx={{ borderRadius: 2, mb: 4 }}>
      <CardHeader
        title="Section: Heart Failure"
        // subheader={`Simulated HF profile: ${name} — ${profile}`}
        titleTypographyProps={{ variant: 'h5', align: 'center' }}
        action={<FavoriteBorderIcon fontSize="large" color="error" />}
      />
      <Divider />
      <CardContent>
        {/* Code + description */}
        <Typography
          variant="subtitle1"
          sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
        >
          <LocalHospitalIcon sx={{ mr: 1 }} /> Code: {code} — {description}
        </Typography>

        {/* NYHA Class */}
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
        >
          <ShowChartIcon sx={{ mr: 1 }} /> NYHA Class
        </Typography>
        <Typography variant="body2" paragraph>
          {nyhaClass}
        </Typography>

        {/* Lab Monitoring */}
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
        >
          <LocalHospitalIcon sx={{ mr: 1 }} /> Lab Monitoring
        </Typography>
        <Table size="small" sx={{ mb: 3 }}>
          <TableHead>
            <TableRow>
              <TableCell>Test</TableCell>
              <TableCell>Result</TableCell>
              <TableCell>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {labs.map((lab, idx) => (
              <TableRow key={idx} hover>
                <TableCell>{lab.test}</TableCell>
                <TableCell>{lab.result}</TableCell>
                <TableCell>{lab.date}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Immunizations */}
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
        >
          <VaccinesIcon sx={{ mr: 1 }} /> Preventive Immunizations
        </Typography>
        <List dense sx={{ mb: 3 }}>
          {immunizations.map((imm, idx) => (
            <ListItem key={idx} disablePadding>
              <ListItemIcon>
                <VaccinesIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={imm} />
            </ListItem>
          ))}
        </List>

        {/* Treatment */}
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
        >
          <LocalHospitalIcon sx={{ mr: 1 }} /> Treatment Plan
        </Typography>
        <List dense sx={{ mb: 3 }}>
          {treatment.map((tx, idx) => (
            <ListItem key={idx} disablePadding>
              <ListItemIcon>
                <LocalHospitalIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={tx} />
            </ListItem>
          ))}
        </List>

        {/* Lifestyle Modifications */}
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
        >
          <SpaIcon sx={{ mr: 1 }} /> Lifestyle Modifications
        </Typography>
        <List dense>
          {lifestyle.map((life, idx) => (
            <ListItem key={idx} disablePadding>
              <ListItemIcon>
                <SpaIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={life} />
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

export default HeartFailureSection;
